import {
  Stack,
  Card,
  Table,
  Badge,
  TextInput,
  Alert,
  Modal,
  Select,
  NumberInput,
  Textarea,
  Group,
  Button,
  ActionIcon,
  Tooltip,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  IconSearch,
  IconAlertCircle,
  IconCheck,
  IconEye,
  IconFile,
  IconPlus,
  IconTruck,
  IconMapPin,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  useEnvios,
  useEmpresas,
  useVehiculos,
  useConductores,
  useEmitirDeCA,
  useOrigenesDestinos,
  getErrorMessage,
} from '../../api/hooks';
import type { Envio, EnvioEstado, UnidadCantidad } from '../../api/types';
import { UNIDAD_CANTIDAD_LABEL, UNIDAD_CANTIDAD_MEANING } from '../../api/types';
import { CatalogPageHeader } from '../../components/CatalogPageHeader';

const ESTADO_COLOR: Record<EnvioEstado, string> = {
  BORRADOR: 'gray',
  EMITIDO: 'green',
  EN_CURSO: 'blue',
  FINALIZADO: 'darkgreen',
  CERRADO: 'dark',
  CANCELADO: 'red',
};

const ESTADO_LABEL: Record<EnvioEstado, string> = {
  BORRADOR: 'Borrador',
  EMITIDO: 'Emitido',
  EN_CURSO: 'En curso',
  FINALIZADO: 'Finalizado',
  CERRADO: 'Cerrado',
  CANCELADO: 'Cancelado',
};

export function EnviosList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: envios = [], isLoading, error } = useEnvios();
  const { data: empresas = [] } = useEmpresas();
  const { data: vehiculos = [] } = useVehiculos();
  const { data: conductores = [] } = useConductores();
  const { data: origenesDestinos = [] } = useOrigenesDestinos();
  const emitir = useEmitirDeCA();

  const [filter, setFilter] = useState('');
  const [estadoFilter, setEstadoFilter] = useState<EnvioEstado | ''>('');

  // Modal Emitir DeCA
  const [emitOpened, { open: openEmit, close: closeEmit }] = useDisclosure(false);
  const [emitTarget, setEmitTarget] = useState<Envio | null>(null);
  const [origenId, setOrigenId] = useState<string>('');
  const [destinoId, setDestinoId] = useState<string>('');
  const [naturaleza, setNaturaleza] = useState<string>('');
  // Antes: `pesoKg` (siempre kg, hard-coded). Ahora: `cantidad` +
  // `unidadCantidad` — admite kg, t, m³, L o palets.
  const [cantidad, setCantidad] = useState<number | ''>('');
  const [unidadCantidad, setUnidadCantidad] = useState<UnidadCantidad>('KG');
  const [bultos, setBultos] = useState<number | ''>(0);
  const [observaciones, setObservaciones] = useState<string>('');

  // Indice por id para joins rapidos
  const empresaById = useMemo(() => Object.fromEntries(empresas.map((e) => [e.id, e])), [empresas]);
  const vehiculoById = useMemo(
    () => Object.fromEntries(vehiculos.map((v) => [v.id, v])),
    [vehiculos],
  );
  const conductorById = useMemo(
    () => Object.fromEntries(conductores.map((c) => [c.id, c])),
    [conductores],
  );
  const filtered = useMemo(
    () =>
      envios.filter((e) => {
        if (estadoFilter && e.estado !== estadoFilter) return false;
        if (!filter) return true;
        const c = empresaById[e.cargadorId]?.razonSocial ?? '';
        const t = empresaById[e.transportistaId]?.razonSocial ?? '';
        const v = vehiculoById[e.vehiculoId]?.matricula ?? '';
        const cn = conductorById[e.conductorId]?.nombre ?? '';
        const all = [e.id, c, t, v, cn, e.estado, e.tipoServicio].join(' ').toLowerCase();
        return all.includes(filter.toLowerCase());
      }),
    [envios, filter, estadoFilter, empresaById, vehiculoById, conductorById],
  );

  const resetEmitForm = () => {
    setOrigenId('');
    setDestinoId('');
    setNaturaleza('');
    setCantidad('');
    setUnidadCantidad('KG');
    setBultos(0);
    setObservaciones('');
  };

  const handleOpenEmit = (e: Envio) => {
    setEmitTarget(e);
    resetEmitForm();
    openEmit();
  };

  // Si llegamos desde el detalle de un envío (botón "Emitir DeCA"),
  // abrimos el modal automaticamente cuando los envios se hayan cargado.
  // El state se limpia con replace para no reabrir en refresh.
  const emitirEnvioIdFromState = (location.state as { emitirEnvioId?: string } | null)
    ?.emitirEnvioId;
  useEffect(() => {
    if (!emitirEnvioIdFromState || isLoading) return;
    const target = envios.find((e) => e.id === emitirEnvioIdFromState && e.estado === 'BORRADOR');
    if (target) handleOpenEmit(target);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emitirEnvioIdFromState, isLoading, envios]);

  const handleEmit = () => {
    if (!emitTarget) return;
    if (!origenId || !destinoId) {
      notifications.show({
        title: 'Faltan origen y destino',
        message: 'Selecciona los puntos de origen y destino del servicio.',
        color: 'orange',
      });
      return;
    }
    if (origenId === destinoId) {
      notifications.show({
        title: 'Origen y destino iguales',
        message: 'Selecciona dos OrigenDestino distintos.',
        color: 'orange',
      });
      return;
    }
    if (
      !naturaleza.trim() ||
      (typeof cantidad === 'number' && cantidad < 0) ||
      (typeof bultos === 'number' && bultos < 0)
    ) {
      notifications.show({
        title: 'Datos incompletos',
        message: 'Indica naturaleza, cantidad (con unidad) y bultos.',
        color: 'orange',
      });
      return;
    }
    emitir.mutate(
      {
        envioId: emitTarget.id,
        servicios: [
          {
            origenId,
            destinoId,
            naturaleza: naturaleza.trim(),
            cantidad: typeof cantidad === 'number' ? cantidad : 0,
            unidadCantidad,
            bultos: typeof bultos === 'number' ? bultos : 0,
            observaciones: observaciones.trim() || undefined,
          },
        ],
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'DeCA emitido',
            message: 'El PDF y el QR se han generado correctamente.',
            color: 'green',
            icon: <IconCheck size={18} />,
          });
          closeEmit();
        },
        onError: (e: unknown) =>
          notifications.show({
            title: 'Error al emitir',
            message: getErrorMessage(e),
            color: 'red',
            icon: <IconAlertCircle size={18} />,
          }),
      },
    );
  };

  const odData = origenesDestinos
    .filter((o) => o.activo !== false)
    .map((o) => ({
      value: o.id,
      label: `${o.nombre} — ${o.poblacion ?? ''} (${o.provincia ?? ''})`,
    }));

  return (
    <Stack gap="md">
      <CatalogPageHeader
        title="Envíos"
        subtitle="Cabeceras de viaje. Cada envío se emite como DeCA con uno o más servicios (origen/destino/mercancía)."
        icon={IconTruck}
        newLabel="Nuevo envío"
        count={envios.length}
        onNew={() => navigate('/envios/nuevo')}
        filters={
          <Group gap="sm">
            <Select
              placeholder="Estado"
              data={[
                { value: '', label: 'Todos los estados' },
                { value: 'BORRADOR', label: 'Borrador' },
                { value: 'EMITIDO', label: 'Emitido' },
                { value: 'EN_CURSO', label: 'En curso' },
                { value: 'FINALIZADO', label: 'Finalizado' },
                { value: 'CERRADO', label: 'Cerrado' },
                { value: 'CANCELADO', label: 'Cancelado' },
              ]}
              value={estadoFilter}
              onChange={(v) => setEstadoFilter((v ?? '') as EnvioEstado | '')}
              clearable
              w={180}
            />
            <TextInput
              placeholder="Buscar por cargadora, vehículo, conductor…"
              leftSection={<IconSearch size={16} />}
              value={filter}
              onChange={(e) => setFilter(e.currentTarget.value)}
              w={300}
            />
          </Group>
        }
      />

      {error && (
        <Alert
          icon={<IconAlertCircle size={20} />}
          color="red"
          variant="light"
          title="No se pudieron cargar los envíos"
        >
          Verifica tu conexión y vuelve a intentarlo.
        </Alert>
      )}

      <Card withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>ID</Table.Th>
              <Table.Th>Tipo</Table.Th>
              <Table.Th>Cargador</Table.Th>
              <Table.Th>Transportista</Table.Th>
              <Table.Th>Vehículo</Table.Th>
              <Table.Th>Conductor</Table.Th>
              <Table.Th>Fecha servicio</Table.Th>
              <Table.Th>Estado</Table.Th>
              <Table.Th style={{ width: 90, textAlign: 'right' }}>Acciones</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && (
              <Table.Tr>
                <Table.Td colSpan={9}>Cargando…</Table.Td>
              </Table.Tr>
            )}
            {!isLoading && !error && filtered.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={9}>
                  No hay envíos con esos filtros. Crea el primero con el botón de arriba.
                </Table.Td>
              </Table.Tr>
            )}
            {filtered.map((e) => {
              const vehiculo = vehiculoById[e.vehiculoId];
              const matriculaTxt = vehiculo
                ? vehiculo.matriculaRemolque
                  ? `${vehiculo.matricula} / ${vehiculo.matriculaRemolque}`
                  : vehiculo.matricula
                : '—';
              return (
                <Table.Tr key={e.id}>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {e.id.slice(0, 8)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light" color="gray">
                      {e.tipoServicio.replace(/_/g, ' ')}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{empresaById[e.cargadorId]?.razonSocial ?? '—'}</Table.Td>
                  <Table.Td>{empresaById[e.transportistaId]?.razonSocial ?? '—'}</Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {matriculaTxt}
                    </Text>
                  </Table.Td>
                  <Table.Td>{conductorById[e.conductorId]?.nombre ?? '—'}</Table.Td>
                  <Table.Td>{e.fechaServicio}</Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light" color={ESTADO_COLOR[e.estado]}>
                      {ESTADO_LABEL[e.estado]}
                    </Badge>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Tooltip label="Ver detalle">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label="Ver detalle"
                          onClick={() => navigate(`/envios/${e.id}`)}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                      {e.estado === 'BORRADOR' && (
                        <Tooltip label="Emitir DeCA">
                          <ActionIcon
                            variant="subtle"
                            color="deca"
                            aria-label="Emitir DeCA"
                            onClick={() => handleOpenEmit(e)}
                          >
                            <IconFile size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal opened={emitOpened} onClose={closeEmit} title="Emitir DeCA" centered size="lg">
        <Stack>
          <Alert color="deca" variant="light" icon={<IconFile size={20} />}>
            <Text fw={500} mb={4}>
              Definir el servicio (MVP: 1 servicio por DeCA)
            </Text>
            <Text size="sm">
              Selecciona origen y destino del catálogo. La mercancía y los bultos se incluyen en el
              PDF del DeCA.
            </Text>
          </Alert>

          <Group grow>
            <Select
              label="Origen"
              placeholder="Selecciona origen"
              searchable
              required
              data={odData}
              leftSection={<IconMapPin size={16} />}
              value={origenId}
              onChange={(v) => setOrigenId(v ?? '')}
            />
            <Select
              label="Destino"
              placeholder="Selecciona destino"
              searchable
              required
              data={odData}
              leftSection={<IconMapPin size={16} />}
              value={destinoId}
              onChange={(v) => setDestinoId(v ?? '')}
            />
          </Group>

          <TextInput
            label="Naturaleza de la mercancía"
            placeholder="Mercancía general paletizada"
            required
            value={naturaleza}
            onChange={(e) => setNaturaleza(e.currentTarget.value)}
          />

          <Group grow align="flex-end" wrap="nowrap">
            <NumberInput
              label="Cantidad"
              min={0}
              decimalScale={3}
              value={cantidad}
              onChange={(v) => setCantidad(typeof v === 'number' ? v : '')}
              style={{ flex: 2, minWidth: 0 }}
            />
            <Select
              label="Unidad"
              data={(['KG', 'T', 'M3', 'L', 'PALET'] as UnidadCantidad[]).map((u) => ({
                value: u,
                label: `${UNIDAD_CANTIDAD_LABEL[u]} (${UNIDAD_CANTIDAD_MEANING[u]})`,
              }))}
              value={unidadCantidad}
              onChange={(v) => v && setUnidadCantidad(v as UnidadCantidad)}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true }}
              style={{ flex: 1, minWidth: 140 }}
            />
            <NumberInput
              label="Bultos"
              min={0}
              value={bultos}
              onChange={(v) => setBultos(typeof v === 'number' ? v : 0)}
              style={{ flex: 1, minWidth: 0 }}
            />
          </Group>

          <Textarea
            label="Observaciones (opcional)"
            autosize
            minRows={2}
            value={observaciones}
            onChange={(e) => setObservaciones(e.currentTarget.value)}
          />

          <Group justify="space-between" mt="md">
            <Button variant="default" onClick={closeEmit} disabled={emitir.isPending}>
              Cancelar
            </Button>
            <Button
              onClick={handleEmit}
              loading={emitir.isPending}
              leftSection={<IconPlus size={16} />}
            >
              Emitir DeCA
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
